import Loader from "@/components/core/layouts/Loader";
import { MoveSignature } from "@/state/transactions/CurrentTransactionEvents";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import { Signature } from "@type/core/dashboard/dashboard.types";
import { UNIVERSIGN_RATIO } from "@utils/Constant";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { getCardDimension } from "@utils/helpers/signatories-helper";
import { CardDimension } from "@type/core/signatory/signatory.types";

interface DraggableCardProps {
  children: React.ReactNode;
}

interface UseSignatoryCardReturn {
  DraggableCard: ({ children }: DraggableCardProps) => JSX.Element;
  signatureImageLeftInitialPosition: number;
}

interface UseSignatoryCardProps {
  transactionType: TransactionType;
  rank: number;
  pageNumber: number;
  signature: Signature | undefined;
  signatures: Signature[];
  setManualPositioning: (value: boolean) => void;
  className?: string;
  onSetSignature: (movedSignature: MoveSignature) => void;
  signatureIdentificator: string;
  isDisabled: boolean;
}

const useSignatoryCard = ({
  transactionType,
  rank,
  pageNumber,
  signature,
  signatures,
  setManualPositioning,
  className = "",
  onSetSignature,
  signatureIdentificator,
  isDisabled,
}: UseSignatoryCardProps): UseSignatoryCardReturn => {
  const ref = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const [pdf, setPdf] = useState<HTMLCanvasElement | null>(null);
  const [leftPanel, setLeftPanel] = useState<HTMLElement | null>(null);
  const [rightPanel, setRightPanel] = useState<HTMLElement | null>(null);

  const [cardDimension, setCardDimension] = useState<CardDimension>();

  const signatureInsert = ref.current?.parentNode as HTMLElement | null;
  const insertWidth =
    signatureInsert?.firstElementChild?.getBoundingClientRect().width ?? 0;
  const isCardVisible =
    !signature?.isMoved ||
    !signature?.page ||
    signature.page === 0 ||
    pageNumber === signature.page;

  useEffect(() => {
    const queryPdf = document.querySelector(
      `#document_${rank} .active canvas`,
    ) as HTMLCanvasElement;

    if (queryPdf) {
      setPdf(queryPdf);
      setLeftPanel(document.querySelector(`#leftPanel_${rank}`) as HTMLElement);
      setRightPanel(
        document.querySelector(`#rightPanel_${rank}`) as HTMLElement,
      );
    } else {
      const observer = new MutationObserver(() => {
        const observedPdf = document.querySelector<HTMLCanvasElement>(
          `#document_${rank} .pdf .active canvas`,
        );
        if (observedPdf) {
          setPdf(observedPdf);
          setLeftPanel(
            document.querySelector(`#leftPanel_${rank}`) as HTMLElement,
          );
          setRightPanel(
            document.querySelector(`#rightPanel_${rank}`) as HTMLElement,
          );
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      observerRef.current = observer;
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [rank, pageNumber]);

  useLayoutEffect(() => {
    if (pdf && !cardDimension) {
      setCardDimension(getCardDimension(pdf));
    }
  }, [pdf]);

  const computeOffsetY = () => {
    const pdfBounds = pdf?.getBoundingClientRect();
    const rightBounds = rightPanel?.getBoundingClientRect();
    const insertBounds = signatureInsert?.getBoundingClientRect();

    if (pdfBounds && rightBounds && insertBounds) {
      return -(
        rightBounds.top -
        pdfBounds.top +
        insertBounds.top -
        rightBounds.top
      );
    }
    return 0;
  };

  const defineUniversignPosition = (
    pdfBounds: DOMRect,
    cardBounds: DOMRect,
    positionX: number,
    positionY: number,
  ) => {
    if (!pdf || !cardDimension) return;
    let isMoved = true;
    if (
      positionX > pdfBounds.width - cardBounds.width ||
      positionY > pdfBounds.height - cardBounds.height
    ) {
      positionX = 0;
      positionY = 0;
      isMoved = false;
    }

    positionX = Math.max(0, positionX);
    positionY = Math.max(0, positionY);

    const ratioX = positionX / pdfBounds.width;
    const ratioY = positionY / pdfBounds.height;

    const UNIVERSIGN_WIDTH = pdf.width / UNIVERSIGN_RATIO;
    const UNIVERSIGN_HEIGHT = pdf.height / UNIVERSIGN_RATIO;

    const adaptedX =
      positionX === 0 ? 0 : Math.floor(UNIVERSIGN_WIDTH * ratioX);
    const adaptedY =
      positionY === 0 ? 0 : Math.floor(UNIVERSIGN_HEIGHT * ratioY);

    const order = signatures.findIndex((s) => s.id === signature?.id);
    const pattern = signature?.pattern
      ? signature.pattern
      : transactionType === "signature"
        ? SignaturePatternEnum.DEFAULT
        : SignaturePatternEnum.SEAL;

    onSetSignature({
      id: signature?.id,
      email: signature?.email ?? null,
      positionX: adaptedX,
      positionY: adaptedY,
      page: isMoved ? pageNumber : undefined,
      order,
      isMoved,
      pattern,
      rank: signature?.rank ?? 1,
      identificator: signature?.identificator,
    });
  };

  const onStop = () => {
    if (
      signature?.identificator !== signatureIdentificator &&
      transactionType === "signature"
    )
      return;
    if (rank !== 1) setManualPositioning(true);

    const pdfBounds = pdf?.getBoundingClientRect();
    const cardBounds = ref.current?.getBoundingClientRect();

    if (pdfBounds && cardBounds && cardDimension) {
      const positionX = cardBounds.left - pdfBounds.left;
      const positionY =
        pdfBounds.height + pdfBounds.top - cardBounds.top - cardBounds.height;

      defineUniversignPosition(pdfBounds, cardBounds, positionX, positionY);
    }
  };
  const computePositionX = (
    positionX: number | undefined,
    pdf: HTMLCanvasElement,
    pdfBounds: DOMRect,
    cardDimension: CardDimension,
  ) => {
    let posX = positionX ?? 0;
    const posXmax = pdf.width - cardDimension.width;
    if (posX > posXmax) {
      posX = posXmax;
    }
    const UNIVERSIGN_WIDTH = pdf.width / UNIVERSIGN_RATIO;
    const ratioX = (posX ?? 0) / UNIVERSIGN_WIDTH;
    return ratioX * pdfBounds.width;
  };

  const computePositionY = (
    positionY: number | undefined,
    pdf: HTMLCanvasElement,
    pdfBounds: DOMRect,
    cardDimension: CardDimension,
  ) => {
    let posY = positionY ?? 0;
    const posYmax = pdf.height - cardDimension?.height;
    if (posY > posYmax) {
      posY = posYmax;
    }
    const UNIVERSIGN_HEIGHT = pdf.height / UNIVERSIGN_RATIO;
    const ratioY = (posY ?? 0) / UNIVERSIGN_HEIGHT;
    return ratioY * pdfBounds.height;
  };

  const DraggableCard = ({ children }: DraggableCardProps) => {
    const pdfBounds = pdf?.getBoundingClientRect();
    const leftBounds = leftPanel?.getBoundingClientRect();
    const rightBounds = rightPanel?.getBoundingClientRect();
    const cardBounds = ref.current?.getBoundingClientRect();

    if (pdf && pdfBounds && leftBounds && rightBounds && cardDimension) {
      const offsetY = computeOffsetY();
      const posX = computePositionX(
        signature?.positionX,
        pdf,
        pdfBounds,
        cardDimension,
      );
      const posY = computePositionY(
        signature?.positionY,
        pdf,
        pdfBounds,
        cardDimension,
      );

      let position = {
        x: Math.ceil(posX - (leftBounds.width + insertWidth)),
        y: Math.ceil(
          offsetY + pdfBounds.height - posY - (cardBounds?.height ?? 0),
        ),
      };

      if (!signature?.isMoved) {
        position = { x: 0, y: 0 };
      }
      return (
        <Draggable
          nodeRef={ref}
          position={position}
          bounds={{
            left: Math.ceil(-(leftBounds.width + insertWidth)),
            top: offsetY,
            right: 0,
            bottom: Math.ceil(
              pdfBounds.height - (cardBounds?.height ?? 0) + offsetY,
            ),
          }}
          onStop={onStop}
          offsetParent={pdf}
          disabled={isDisabled}
        >
          <div
            ref={ref}
            className={`app-signatory-card ${className} ${isDisabled ? "signatory-card-disabled" : ""}`}
            style={{
              visibility: isCardVisible ? "visible" : "hidden",
              width: cardDimension?.width,
              height: cardDimension?.height,
            }}
            title={signature?.name}
            data-test="card"
            data-alt={
              transactionType === "signature"
                ? (signature?.name ?? "").trim()
                : signature?.email
            }
          >
            {children}
          </div>
        </Draggable>
      );
    } else {
      return (
        <Loader className={`justify-content-center py-16 w-30 ${className}`} />
      );
    }
  };

  return {
    DraggableCard,
    signatureImageLeftInitialPosition: insertWidth,
  };
};

export default useSignatoryCard;
